import { defineComponent as _defineComponent } from 'vue'
import { vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, unref as _unref, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, vModelCheckbox as _vModelCheckbox, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "excel-option" }
const _hoisted_2 = {
  key: 1,
  class: "readonly"
}
const _hoisted_3 = { class: "body-label" }
const _hoisted_4 = { class: "items" }

import {computed, onMounted, reactive, ref, watch} from "vue";
import {useRouter} from "vue-router";
import SogPage from "@/shared/ui/SogPage.vue";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardTitle from "@/shared/ui/SogCardTitle.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import SogFlexColumn from "@/shared/ui/SogFlexColumn.vue";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import SogDropDownList from "@/shared/ui/SogDropDownList.vue";
import SogDataLabel from "@/shared/ui/SogDataLabel.vue";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import notificationService from "@/shared/services/NotificationService";
import apiRegistrationInquiry from "@/modules/RegistrationInquiry/api";
import VehicleRegistrationInquiry, {isInquiryEditable, VehicleRegistrationInquiryItem} from "@/shared/model/VehicleRegistrationInquiry";

interface Props {
  id?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'RegistrationInquiryDetailPage',
  props: {
    id: { default: undefined }
  },
  setup(__props: any) {

const props = __props;

const _router = useRouter();
const _fields = {text: "value", value: "id"};
const _inquiry = ref<VehicleRegistrationInquiry | undefined>(undefined);
const _items = ref<VehicleRegistrationInquiryItem[]>([]);
const _recipients = ref("");
const _subject = ref("");
const _body = ref("");
const _mailType = ref("");
const _attachExcel = ref(true);

// Arten, die ein Entwurf haben kann (Erinnerungen entstehen nur aus dem Dienst)
const mailTypeItems = reactive([
  {id: "InsuranceCopy", value: "Versicherungsschein anfordern"},
  {id: "Urgent", value: "Dringend (Schadensfall)"},
  {id: "DoubleNumber", value: "Policennummer mehrfach"},
  {id: "ForeignPolicy", value: "Fremde Police"},
  {id: "InternalNumber", value: "Interne Nummer der Zulassungsstelle"},
]);

const _editable = computed(() => isInquiryEditable(_inquiry.value));

async function load() {
  if (!props.id) return;
  const t_res = await apiRegistrationInquiry.load(props.id);
  if (!t_res) {
    _inquiry.value = undefined;
    _items.value = [];
    return;
  }
  _inquiry.value = t_res.inquiry;
  _items.value = t_res.items;
  _recipients.value = t_res.inquiry.recipients ?? "";
  _subject.value = t_res.inquiry.subject ?? "";
  _body.value = t_res.inquiry.bodyText ?? "";
  _mailType.value = t_res.inquiry.mailTypeId ?? "";
  _attachExcel.value = t_res.inquiry.attachExcel !== false;
}

async function save(): Promise<boolean> {
  if (!_inquiry.value?.id) return false;
  return await apiRegistrationInquiry.update(_inquiry.value.id, _recipients.value, _subject.value, _body.value, _attachExcel.value);
}

async function onSave() {
  if (await save()) {
    notificationService.success("Änderungen wurden gespeichert");
    await load();
  }
}

function onApprove() {
  const t_id = _inquiry.value?.id;
  if (!t_id) return;
  notificationService.showYesNo(
    "Mail freigeben",
    "Soll die Mail an " + _recipients.value + " freigegeben werden? Sie wird danach automatisch versendet.",
    async () => {
      if (_editable.value && !(await save())) return;
      const t_res = await apiRegistrationInquiry.approve(t_id);
      if (t_res.successful) {
        notificationService.success("Mail wurde freigegeben" + (t_res.message ? ". " + t_res.message : ""));
      } else if (t_res.message?.startsWith("Alle angefragten")) {
        notificationService.success(t_res.message);
      }
      await _router.push("/registrationinquiry/list");
    });
}

function onDiscard() {
  const t_id = _inquiry.value?.id;
  if (!t_id) return;
  notificationService.showYesNo(
    "Mail verwerfen",
    "Soll der Entwurf verworfen werden? Die Zulassungen werden danach 30 Tage lang nicht erneut angefragt.",
    async () => {
      if (await apiRegistrationInquiry.discard(t_id)) {
        notificationService.success("Entwurf wurde verworfen");
        await _router.push("/registrationinquiry/list");
      }
    });
}

// Art gewählt: vor der Bestätigung die bisherige Art zeigen, nach „Ja" die Vorlage der neuen Art übernehmen
watch(_mailType, (newType) => {
  const t_inquiry = _inquiry.value;
  if (!t_inquiry?.id || !_editable.value || newType === t_inquiry.mailTypeId) return;
  _mailType.value = t_inquiry.mailTypeId ?? "";
  const t_label = mailTypeItems.find(t => t.id === newType)?.value ?? newType;
  notificationService.showYesNo(
    "Art ändern",
    `Betreff und Text werden durch die Vorlage der Art „${t_label}“ ersetzt. Manuelle Änderungen am Text gehen verloren. Fortfahren?`,
    async () => {
      const t_ok = await apiRegistrationInquiry.changeMailType(t_inquiry.id ?? "", newType);
      if (t_ok) {
        notificationService.success("Art wurde geändert");
        await load();
      }
    });
});

watch(() => props.id, load);

onMounted(load);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(SogPage, { title: "Recherche-Mail" }, {
    default: _withCtx(() => [
      _withDirectives(_createVNode(SogCard, null, {
        default: _withCtx(() => [
          _createVNode(SogCardActions, { "is-toolbar": true }, {
            default: _withCtx(() => [
              _withDirectives(_createVNode(SogCardActionButton, {
                "mdi-icon": "mdi-content-save",
                tooltip: "Änderungen speichern",
                onClick: onSave
              }, null, 512), [
                [_vShow, _editable.value]
              ]),
              _withDirectives(_createVNode(SogCardActionButton, {
                "mdi-icon": "mdi-send-check",
                tooltip: "Freigeben (Versand durch den Hintergrunddienst)",
                onClick: onApprove
              }, null, 512), [
                [_vShow, _editable.value]
              ]),
              _withDirectives(_createVNode(SogCardActionButton, {
                "mdi-icon": "mdi-delete",
                tooltip: "Verwerfen",
                onClick: onDiscard
              }, null, 512), [
                [_vShow, _editable.value]
              ]),
              _createVNode(SogCardActionButton, {
                kind: _unref(SogButtonKind).Back
              }, null, 8, ["kind"])
            ]),
            _: 1
          }),
          _createVNode(SogCardTitle, null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_inquiry.value?.officeName) + " · " + _toDisplayString(_inquiry.value?.mailTypeText) + " · " + _toDisplayString(_inquiry.value?.statusText), 1)
            ]),
            _: 1
          }),
          _createVNode(SogCardContent, null, {
            default: _withCtx(() => [
              _createVNode(SogFlexColumn, null, {
                default: _withCtx(() => [
                  _withDirectives(_createElementVNode("div", { class: "error" }, _toDisplayString(_inquiry.value?.errorText), 513), [
                    [_vShow, _inquiry.value?.errorText]
                  ]),
                  (_editable.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createElementVNode("label", _hoisted_1, [
                          _withDirectives(_createElementVNode("input", {
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_attachExcel).value = $event)),
                            type: "checkbox"
                          }, null, 512), [
                            [_vModelCheckbox, _attachExcel.value]
                          ]),
                          _cache[5] || (_cache[5] = _createTextVNode(" Excel-Liste der Vorgänge beilegen ", -1))
                        ]),
                        _createVNode(SogDropDownList, {
                          modelValue: _mailType.value,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_mailType).value = $event)),
                          placeholder: "Art der Mail (ändert die Vorlage)",
                          "data-source": mailTypeItems,
                          fields: _fields,
                          "popup-width": "320px"
                        }, null, 8, ["modelValue", "data-source"]),
                        _createVNode(SogTextBox, {
                          modelValue: _recipients.value,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_recipients).value = $event)),
                          placeholder: "Empfänger (mit ; getrennt)",
                          width: "100%"
                        }, null, 8, ["modelValue"]),
                        _createVNode(SogTextBox, {
                          modelValue: _subject.value,
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_subject).value = $event)),
                          placeholder: "Betreff",
                          width: "100%"
                        }, null, 8, ["modelValue"]),
                        _createVNode(SogTextBox, {
                          modelValue: _body.value,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_body).value = $event)),
                          placeholder: "Text",
                          multiline: true,
                          width: "100%"
                        }, null, 8, ["modelValue"])
                      ], 64))
                    : (_openBlock(), _createElementBlock("table", _hoisted_2, [
                        _createElementVNode("tbody", null, [
                          _createElementVNode("tr", null, [
                            _cache[6] || (_cache[6] = _createElementVNode("td", null, "Art", -1)),
                            _createElementVNode("td", null, [
                              _createVNode(SogDataLabel, {
                                value: _inquiry.value?.mailTypeText
                              }, null, 8, ["value"])
                            ])
                          ]),
                          _createElementVNode("tr", null, [
                            _cache[7] || (_cache[7] = _createElementVNode("td", null, "Excel-Liste", -1)),
                            _createElementVNode("td", null, [
                              _createVNode(SogDataLabel, {
                                value: _inquiry.value?.attachExcel === false ? 'nein' : 'ja'
                              }, null, 8, ["value"])
                            ])
                          ]),
                          _createElementVNode("tr", null, [
                            _cache[8] || (_cache[8] = _createElementVNode("td", null, "Empfänger", -1)),
                            _createElementVNode("td", null, [
                              _createVNode(SogDataLabel, {
                                value: _inquiry.value?.recipients
                              }, null, 8, ["value"])
                            ])
                          ]),
                          _createElementVNode("tr", null, [
                            _cache[9] || (_cache[9] = _createElementVNode("td", null, "Betreff", -1)),
                            _createElementVNode("td", null, [
                              _createVNode(SogDataLabel, {
                                value: _inquiry.value?.subject
                              }, null, 8, ["value"])
                            ])
                          ]),
                          _createElementVNode("tr", null, [
                            _cache[10] || (_cache[10] = _createElementVNode("td", null, "Text", -1)),
                            _createElementVNode("td", _hoisted_3, [
                              _createVNode(SogDataLabel, {
                                value: _inquiry.value?.bodyText
                              }, null, 8, ["value"])
                            ])
                          ])
                        ])
                      ])),
                  _createElementVNode("table", _hoisted_4, [
                    _cache[11] || (_cache[11] = _createElementVNode("thead", null, [
                      _createElementVNode("tr", null, [
                        _createElementVNode("th", null, "Kennzeichen"),
                        _createElementVNode("th", null, "Nummer"),
                        _createElementVNode("th", null, "Hinweis")
                      ])
                    ], -1)),
                    _createElementVNode("tbody", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_items.value, (item) => {
                        return (_openBlock(), _createElementBlock("tr", {
                          key: item.id
                        }, [
                          _createElementVNode("td", null, _toDisplayString(item.kennzeichen), 1),
                          _createElementVNode("td", null, _toDisplayString(item.nummer), 1),
                          _createElementVNode("td", null, _toDisplayString(item.isUnknown ? "angefragt" : "bereits vorhanden (zur Klärung genannt)"), 1)
                        ]))
                      }), 128))
                    ])
                  ])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 512), [
        [_vShow, _inquiry.value]
      ])
    ]),
    _: 1
  }))
}
}

})